import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, ExternalLink, Loader2, Save, GraduationCap, Globe, RefreshCw, Link2 } from "lucide-react";
import api, { resolveImg, formatApiError } from "../../lib/api";

function DocCard({ doc, onChange, onDelete }) {
  const [text, setText] = useState(doc.extracted_text || "");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const dirty = text !== (doc.extracted_text || "");
  const isUrl = doc.type === "url";

  useEffect(() => { setText(doc.extracted_text || ""); }, [doc.extracted_text]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/knowledge/${doc.id}`, { extracted_text: text });
      onChange(data);
      toast.success("Testo aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleActive = async () => {
    try {
      const { data } = await api.patch(`/admin/knowledge/${doc.id}`, { active: !doc.active });
      onChange(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/admin/knowledge/${doc.id}/refresh`);
      onChange(data);
      toast.success("Fonte aggiornata dal link");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };

  const Icon = isUrl ? Globe : (doc.type === "pdf" ? FileText : ImageIcon);
  const iconClass = isUrl ? "bg-fir/10 text-fir" : (doc.type === "pdf" ? "bg-red-50 text-red-500" : "bg-gold/15 text-gold");

  return (
    <div data-testid={`kb-card-${doc.id}`} className="bg-white rounded-2xl border border-stone p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${iconClass}`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-fir-deep truncate" title={doc.title}>{doc.title}</p>
          <p className="text-xs text-fir-deep/50 truncate">
            {isUrl
              ? <>Link · {doc.last_fetched_at ? `aggiornato ${new Date(doc.last_fetched_at).toLocaleString("it-IT")}` : "mai aggiornato"}</>
              : <>{doc.type === "pdf" ? "PDF" : "Immagine"} · {new Date(doc.created_at).toLocaleDateString("it-IT")}</>}
          </p>
          {isUrl && doc.last_fetch_error ? (
            <p className="text-xs text-red-500 mt-0.5" data-testid={`kb-url-error-${doc.id}`}>⚠ {doc.last_fetch_error}</p>
          ) : null}
        </div>
        {isUrl && (
          <button onClick={refresh} disabled={refreshing} data-testid={`kb-refresh-${doc.id}`}
            className="text-fir hover:text-gold p-1.5 disabled:opacity-40" title="Aggiorna ora dal link">
            {refreshing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
          </button>
        )}
        <a href={isUrl ? doc.source_url : resolveImg(doc.file_url)} target="_blank" rel="noreferrer" data-testid={`kb-open-${doc.id}`}
          className="text-fir-deep/60 hover:text-gold p-1.5" title={isUrl ? "Apri link" : "Apri file"}><ExternalLink size={16} /></a>
        <button onClick={onDelete} data-testid={`kb-delete-${doc.id}`}
          className="text-fir-deep/60 hover:text-red-500 p-1.5" title="Elimina"><Trash2 size={16} /></button>
      </div>

      <textarea
        data-testid={`kb-text-${doc.id}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Testo estratto (modificabile). L'assistente userà questo contenuto per i suggerimenti."
        className="mt-3 w-full text-sm rounded-xl border border-stone bg-ivory/40 p-3 outline-none focus:border-gold resize-y"
      />

      <div className="mt-3 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-fir-deep/70 cursor-pointer" data-testid={`kb-active-${doc.id}`}>
          <input type="checkbox" checked={!!doc.active} onChange={toggleActive} className="accent-gold h-4 w-4" />
          Attivo per l'assistente
        </label>
        <button onClick={save} disabled={!dirty || saving} data-testid={`kb-save-${doc.id}`}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-fir text-ivory text-sm disabled:opacity-40 hover:bg-fir-deep transition-colors">
          {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Salva testo
        </button>
      </div>
    </div>
  );
}

export default function Knowledge() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [urlTitle, setUrlTitle] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/knowledge"); setDocs(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocs((d) => [data, ...d]);
        if (data.extract_error) toast.warning(`${data.title}: ${data.extract_error}`);
        else toast.success(`${data.title} caricato ed elaborato`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload fallito");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const addUrl = async () => {
    if (!url.trim()) { toast.error("Inserisci un URL"); return; }
    setAddingUrl(true);
    try {
      const { data } = await api.post("/admin/knowledge/url", { url: url.trim(), title: urlTitle.trim() || null });
      setDocs((d) => [data, ...d]);
      setUrl(""); setUrlTitle("");
      toast.success("Fonte da link aggiunta e analizzata dall'IA");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAddingUrl(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/knowledge/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      toast.success("Documento eliminato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const upsert = (updated) => setDocs((d) => d.map((x) => (x.id === updated.id ? updated : x)));

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-1">
        <GraduationCap className="text-gold" size={26} />
        <h1 className="font-display text-3xl text-fir-deep">Assistente IA — Base Conoscenza</h1>
      </div>
      <p className="text-fir-deep/60 mb-6 text-sm">
        Carica PDF/foto dei depliant locali oppure aggiungi link (siti eventi, sagre, attività come rafting): l'assistente
        legge questi contenuti e li suggerisce agli ospiti. I link si <b>aggiornano da soli ogni 24 ore</b>.
      </p>

      <button
        data-testid="kb-upload-btn"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full mb-5 rounded-2xl border-2 border-dashed border-wood/50 py-8 flex flex-col items-center justify-center text-fir-deep/60 hover:border-gold hover:text-gold transition-colors"
      >
        {uploading ? <Loader2 className="animate-spin" size={28} /> : <Upload size={28} />}
        <span className="mt-2 font-medium">{uploading ? "Elaborazione in corso…" : "Carica PDF o immagini"}</span>
        <span className="text-xs mt-1 text-fir-deep/40">PDF, JPG, PNG, WEBP · max 20MB · più file insieme</span>
      </button>
      <input ref={inputRef} type="file" accept=".pdf,image/*" multiple className="hidden"
        onChange={(e) => handleFiles(e.target.files)} data-testid="kb-file-input" />

      <div className="mb-8 rounded-2xl border border-stone bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="text-fir" size={18} />
          <h2 className="font-medium text-fir-deep">Fonti da link (auto-aggiornanti)</h2>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-2">
          <div className="space-y-2">
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="kb-url-input"
              placeholder="https://… (es. pagina eventi Val di Sole, rafting, sagre)"
              className="input-alpine w-full" />
            <input type="text" value={urlTitle} onChange={(e) => setUrlTitle(e.target.value)} data-testid="kb-url-title-input"
              placeholder="Titolo (opzionale)" className="input-alpine w-full" />
          </div>
          <button onClick={addUrl} disabled={addingUrl} data-testid="kb-url-add-btn"
            className="gold-btn px-5 py-3 rounded-xl h-fit flex items-center justify-center gap-2 disabled:opacity-50">
            {addingUrl ? <Loader2 className="animate-spin" size={16} /> : <Globe size={16} />} Aggiungi link
          </button>
        </div>
        <p className="text-xs text-fir-deep/45 mt-2">
          L'IA analizza la pagina ed estrae eventi, date, luoghi e prezzi. Funziona meglio su pagine testuali
          (programma/listino); sui siti molto dinamici potrebbe estrarre meno dettagli (te lo segnaliamo sulla scheda).
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-fir" /></div>
      ) : docs.length === 0 ? (
        <p className="text-center text-fir-deep/40 py-10" data-testid="kb-empty">Nessuna fonte. Aggiungi un depliant o un link qui sopra.</p>
      ) : (
        <div className="space-y-4" data-testid="kb-list">
          {docs.map((d) => (
            <DocCard key={d.id} doc={d} onChange={upsert} onDelete={() => remove(d.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
